import { useEffect, useRef, useState } from 'react';
import { ago } from '@/shared/format/ago';
import { strings } from '@/shared/strings';
import { OfflineIcon, RefreshIcon } from '@/ui/icons';
import { createPortal } from 'react-dom';
import { HEADER_SLOT_ID } from '@/ui/layout/headerSlot';
import { usePhoneLayout } from '@/ui/layout/usePhoneLayout';
import { DURATION, EASE, reducedMotion } from '@/ui/motion/motion';
import styles from './Freshness.module.css';

export interface FreshnessProps {
  /** A list's own freshness: on a phone its button goes to the header's top right. */
  inHeader?: boolean;
  /** When the data on screen was fetched, in epoch milliseconds; nought for never. */
  updatedAt: number;
  /** A fetch in flight: the icon spins, once it has run long enough to be worth saying. */
  fetching: boolean;
  onRefresh: () => void;
  /**
   * The last fetch failed and what is on screen is the saved copy. Said in
   * words, so nobody acts on a conversation or a movement they think is live.
   */
  stale?: boolean;
}

/** A refresh quicker than this shows nothing at all. */
export const SPIN_DELAY_MS = 300;

/**
 * The refresh button and the line beside it saying how fresh the list is, on
 * every list, the job orders and Lab Notes. Request of 4 October 2026, section 8,
 * steadied by the request that followed it, section 2.
 *
 * The words never change during a refresh, only the icon: the line used to
 * switch to "Updating" and back, a different width each time, and everything
 * beside it jumped. Now the line keeps saying when the list was last updated,
 * in a box of fixed width, and the icon spins only once a refresh has taken
 * longer than SPIN_DELAY_MS, so a quick one does not flash.
 */
export function Freshness({
  updatedAt,
  fetching,
  onRefresh,
  stale = false,
  inHeader = false,
}: FreshnessProps) {
  // On a phone, a list's refresh button sits in the header's top right corner.
  // Request of 5 October 2026.
  const phone = usePhoneLayout();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(inHeader ? document.getElementById(HEADER_SLOT_ID) : null);
  }, [inHeader]);
  const [now, setNow] = useState(() => Date.now());
  const [spinning, setSpinning] = useState(false);
  const icon = useRef<HTMLSpanElement>(null);

  /*
   * The tap is acknowledged at once: the arrows make one quick turn, so a
   * refresh too fast to show a spinner still answers the finger. Request of
   * 3 October 2026. A slow refresh carries on into the steady spin above.
   */
  function acknowledge() {
    const element = icon.current;
    if (element === null || typeof element.animate !== 'function') return;
    element.animate(
      reducedMotion()
        ? [{ opacity: 0.4 }, { opacity: 1 }]
        : [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: DURATION.slow, easing: EASE.move },
    );
  }

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!fetching) {
      setSpinning(false);
      return;
    }
    const slow = setTimeout(() => setSpinning(true), SPIN_DELAY_MS);
    return () => clearTimeout(slow);
  }, [fetching]);

  // A fetch that has just landed is "just now", whatever the last tick said.
  const shownNow = Math.max(now, updatedAt);
  const words = stale
    ? strings.freshness.offline
    : updatedAt > 0
      ? `${strings.freshness.updated} ${ago(updatedAt, shownNow)}`
      : '';

  const button = (
    <button
      type="button"
      className={`${styles.refresh} ${spinning ? styles.spinning : ''} ${phone && slot !== null ? styles.header : ''}`}
      aria-label={spinning ? strings.freshness.updating : strings.freshness.refresh}
      title={words === '' ? strings.freshness.refresh : words}
      aria-busy={fetching}
      onClick={() => {
        acknowledge();
        if (!fetching) onRefresh();
      }}
    >
      <span ref={icon} className={styles.icon}>
        <RefreshIcon size={phone && slot !== null ? 18 : 16} />
      </span>
    </button>
  );

  if (phone && slot !== null) {
    return (
      <>
        {stale && (
          <span className={`${styles.when} ${styles.stale}`} role="status">
            <OfflineIcon size={13} />
            {words}
          </span>
        )}
        {createPortal(button, slot)}
      </>
    );
  }

  return (
    <div className={styles.fresh}>
      <span className={`${styles.when} ${stale ? styles.stale : ''}`} role="status" title={words}>
        {stale && <OfflineIcon size={13} />}
        {words}
      </span>
      {button}
    </div>
  );
}
